<template>
  <AddDeviceButton
    v-if="isPlaceholder || !deviceIsAttached"
    class="opacity-50 pointer-events-none"
    :label="device"
    :icon="device"
    @click="handleExternalDeviceConnection"
  />
  <template v-else>
    <AddDeviceButton
      v-if="deviceIsAttached && !video && !loading"
      :class="{ 'opacity-50 pointer-events-none': !deviceIsAttached }"
      :label="device === 'Monitor' ? 'Diagnostic imaging' : device"
      :icon="device"
      @click="handleExternalDeviceConnection"
    />

    <div class="relative w-full h-0 pt-55" v-if="(deviceIsAttached && video) || loading">
      <div class="absolute h-full w-full top-0 left-0">
        <video ref="video" muted autoplay class="relative w-full h-full z-10" />
        <ViceLoader class="top-0" v-if="loading" />
        <button
          class="block right-0 top-0 absolute z-20"
          :title="`Stop ${device} Stream`"
          @click="handleExternalDeviceConnection"
        >
          <TrashIcon class="h-6 w-6 shadow-filter" />
        </button>
        <span class="text-sm label-text">
          {{ device === 'Monitor' ? 'Diagnostic Imaging' : device }}
        </span>
      </div>
    </div>
  </template>
</template>

<script>
import { computed, ref, onMounted, watch } from 'vue';
import { TrashIcon } from '@heroicons/vue/solid';
import useLocalStreamingDevicesStore from '@/Stores/useLocalStreamingDevicesStore';
import {
  OTOSCOPE_LABELS,
  DERMATOSCOPE_LABELS,
  MONITOR_LABELS,
  EXAM_CAM_LABELS,
  EXTERNAL_CONNECTED_VIDEO_DEVICES,
} from '@/Utils/vice-constants';
import ViceLoader from '@/Components/Loaders/ViceLoader.vue';

import useSessionStore from '@/Stores/useSessionStore';
import AddDeviceButton from '../Buttons/AddDeviceButton.vue';

export default {
  name: 'PreConsultationExternalVideoDevicePreview',
  props: {
    device: {
      type: String,
      required: true,
    },
  },
  components: {
    AddDeviceButton,
    ViceLoader,
    TrashIcon,
  },
  setup(_props) {
    const isPlaceholder = computed(() => _props.device === 'Medical Device');
    const multiCameraDevicesStore = useLocalStreamingDevicesStore();
    const sessionStore = useSessionStore();
    const loading = ref(false);
    const video = ref();

    let deviceLabels = [];
    switch (_props.device) {
      case EXTERNAL_CONNECTED_VIDEO_DEVICES.OTOSCOPE:
        deviceLabels = OTOSCOPE_LABELS;
        break;
      case EXTERNAL_CONNECTED_VIDEO_DEVICES.DERMATOSCOPE:
        deviceLabels = DERMATOSCOPE_LABELS;
        break;
      case EXTERNAL_CONNECTED_VIDEO_DEVICES.MONITOR:
        deviceLabels = MONITOR_LABELS;
        break;
      case EXTERNAL_CONNECTED_VIDEO_DEVICES.EXAM_CAM:
        deviceLabels = EXAM_CAM_LABELS;
        break;
      default:
    }

    const isCurrentlyStreaming = computed(() =>
      Boolean(
        multiCameraDevicesStore.allExternalConnectedVideoDevices.find(
          (deviceName) => deviceName === _props.device,
        ),
      ),
    );

    const isConnected = computed(
      () =>
        !!multiCameraDevicesStore.groupedDeviceAsAudioAndVideo.VIDEO.Camera.find((device) =>
          deviceLabels.some((singleLabel) => device.label.includes(singleLabel)),
        ),
    );

    const deviceIsAttached = computed(() =>
      multiCameraDevicesStore.groupedDeviceAsAudioAndVideo.VIDEO.Camera.find((device) =>
        deviceLabels.some((singleLabel) => device.label.includes(singleLabel)),
      ),
    );

    const devicePredefined = computed(
      () =>
        !!multiCameraDevicesStore.clinicianDevicesForSession.clinician_devices.some(
          (device) => device.device_type_label === _props.device,
        ),
    );

    const deviceCached = computed(
      () =>
        !!multiCameraDevicesStore.cachedExternalVideoDevices.find(
          (device) => device.name === _props.device,
        ),
    );

    const mediaDeviceId = computed(() => {
      let deviceId = null;

      const device = multiCameraDevicesStore.groupedDeviceAsAudioAndVideo.VIDEO.Camera.find(
        (_device) => deviceLabels.some((singleLabel) => _device.label.includes(singleLabel)),
      );

      if (device) {
        deviceId = device.deviceId;
      }

      return deviceId;
    });

    // Attach local video stream, either from devices saved by clinician, or from manually triggering the stream
    watch(isCurrentlyStreaming, (newVal, oldVal) => {
      if (newVal && !oldVal && mediaDeviceId?.value) {
        selectCameraById(mediaDeviceId.value);
      }
    });

    watch(isConnected, async (newVal) => {
      if (newVal) {
        await addCachedDevice();
      } else {
        video.value = null;
        await multiCameraDevicesStore.removeExternalVideoDevice(_props.device, false);
        loading.value = false;
      }
    });

    const handleExternalDeviceConnection = async () => {
      loading.value = true;
      multiCameraDevicesStore.markSessionSetupAsChanged();
      try {
        if (isCurrentlyStreaming.value) {
          await multiCameraDevicesStore.removeExternalVideoDevice(_props.device);
          loading.value = false;
          video.value = null;
        } else if (mediaDeviceId.value) {
          await multiCameraDevicesStore.addExternalVideoDevice(
            sessionStore.sessionId,
            _props.device,
            deviceLabels,
          );
          loading.value = false;
        }
      } catch (error) {
        console.error({ error });
      }
    };

    const startCameraStream = async (deviceId) => {
      try {
        const constraints = {
          video: {
            deviceId: deviceId ? { exact: deviceId } : undefined,
          },
        };

        video.value.srcObject = await navigator.mediaDevices.getUserMedia(constraints);
      } catch (error) {
        console.error('Error accessing camera:', error);
      }
    };

    const selectCameraById = async (cameraId) => {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter((_device) => _device.kind === 'videoinput');

      const selectedCamera = videoDevices.find((_device) => _device.deviceId === cameraId);
      if (selectedCamera) {
        await startCameraStream(selectedCamera.deviceId);
      } else {
        console.error('Camera with the given ID not found.');
      }
    };

    const addCachedDevice = async () => {
      if (isConnected.value && deviceCached.value) {
        loading.value = true;

        await multiCameraDevicesStore.addExternalVideoDevice(
          sessionStore.sessionId,
          _props.device,
          deviceLabels,
        );

        await startCameraStream(mediaDeviceId.value);
        loading.value = false;
      }
    };

    onMounted(async () => {
      const isDeviceSetupChanged = multiCameraDevicesStore.isSessionSetupWasChanged;
      // Remove from cache if setup not changed and device was not predefined
      if (!isDeviceSetupChanged && !devicePredefined.value) {
        multiCameraDevicesStore.removeExternalVideoDeviceFromCache(_props.device);
      }

      // Add to cache and start if setup not changed and device predefined
      if (!isDeviceSetupChanged && devicePredefined.value) {
        multiCameraDevicesStore.addExternalVideoDeviceToCache(_props.device, deviceLabels);
        await addCachedDevice();
      }

      // Start device from cache if setup was changed
      if (isDeviceSetupChanged) await addCachedDevice();

      setTimeout(() => {
        console.log('mounded', _props.device, isCurrentlyStreaming.value);
      }, 3000);
    });

    return {
      isPlaceholder,
      loading,
      deviceIsAttached,
      video,
      multiCameraDevicesStore,
      handleExternalDeviceConnection,
    };
  },
};
</script>
<style scoped>
.pt-55 {
  padding-top: 55.59%;
}
.label-text {
  display: block;
  position: absolute;
  bottom: 0;
  left: 4px;
  z-index: 10;
}
</style>
